<script setup lang="ts">
  import { computed } from 'vue';
  import { RouterLink, useRoute } from 'vue-router';

  import type { Task } from '@stuut/api/models';
  import { humanize } from '@stuut/common/src/utils';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import PriorityIndicator from '@/components/common/PriorityIndicator.vue';
  import TaskReplyWarning from '@/components/tasks/TaskReplyWarning.vue';
  import TasksAssigneeSelect from '@/components/tasks/TasksAssigneeSelect.vue';

  interface Props {
    task: Task;
  }

  const props = defineProps<Props>();
  const route = useRoute();

  const classifications = computed(
    () => props.task?.classifications?.map((c) => humanize(c)).join(', ') || '',
  );

  const taskRoute = computed(() => ({
    query: { ...route.query, task_drawer_id: props.task?.id },
  }));

  const priorityRowClass = computed(() => {
    switch (props.task?.priority) {
      case 'urgent':
        return 'bg-red-700 text-white';
      case 'high':
        return 'bg-error/15 text-error';
      case 'medium':
        return 'bg-warning/20 text-warning-800';
      case 'low':
      default:
        return 'bg-muted text-muted-foreground';
    }
  });
</script>

<template>
  <div
    class="border-border bg-card text-card-foreground w-full relative overflow-hidden border hover:bg-muted/40 transition-colors"
  >
    <div
      class="flex items-center text-3xs gap-x-1.5 px-4 py-1 font-semibold uppercase tracking-[0.15em] border-dotted border-b-1"
      :class="priorityRowClass"
    >
      <PriorityIndicator v-if="task.priority !== 'urgent'" :priority="task.priority" size="2xs" />
      <span>{{ task.priority }} priority task</span>
    </div>

    <div class="flex items-center justify-between gap-x-4 px-4 py-3">
      <div class="flex flex-col gap-y-2.5">
        <RouterLink
          :to="taskRoute"
          class="flex items-center hover:underline focus:outline-none flex-1 min-w-0"
        >
          <Tooltip :delay-duration="250">
            <TooltipTrigger class="text-left">
              <span class="text-xs line-clamp-2 font-semibold">
                {{ task.label }}
              </span>
            </TooltipTrigger>

            <TooltipContent v-if="task?.label" class="max-w-[300px]">
              {{ task.label }}
            </TooltipContent>
          </Tooltip>
        </RouterLink>

        <div
          v-if="task.customDescription?.label"
          class="flex items-center gap-x-1.5 truncate text-2xs"
        >
          <i class="shrink-0" :class="task.customDescription?.icon" />
          <span class="truncate mt-0.5">{{ task.customDescription?.label }}</span>
        </div>

        <Tooltip v-else-if="classifications" :delay-duration="750">
          <TooltipTrigger as-child>
            <span class="truncate text-xs text-left text-2xs">{{ classifications }}</span>
          </TooltipTrigger>

          <TooltipContent side="left" class="max-w-[300px]">
            {{ classifications }}
          </TooltipContent>
        </Tooltip>

        <span v-else class="text-xs text-muted-foreground">--</span>
      </div>

      <div class="flex items-center gap-x-3 shrink-0">
        <TaskReplyWarning :task="task" compact show-any-task-type />
        <TasksAssigneeSelect size="xs" :task="task" :show-avatar-only="true" />
      </div>
    </div>
  </div>
</template>
